
import { useState } from "react";
import { ImageIcon, Camera } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import Image from "@/components/ui/image";
import { useLogoManagement } from "../hooks/useLogoManagement";
import { LogoDialog } from "../LogoDialog";
import { useEffect } from "react";

interface CompanyLogoUploadProps {
  companyId?: string;
  companyName: string;
  logoUrl?: string;
  onLogoUpdate?: (newLogoUrl: string | null) => void;
}

export function CompanyLogoUpload({
  companyId,
  companyName,
  logoUrl,
  onLogoUpdate
}: CompanyLogoUploadProps) {
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const [canEditLogo, setCanEditLogo] = useState(false);
  const { hasRole } = useUserRole();
  
  const { getCachedLogoUrl, handleFileUpload, handleLogoUrlUpdate, handleLogoRemove, isUploading } = useLogoManagement({
    companyId: companyId || '',
    onLogoUpdate: (newLogoUrl) => {
      onLogoUpdate?.(newLogoUrl);
    }
  });

  const cachedLogoUrl = getCachedLogoUrl(logoUrl || null) || undefined;

  // Check if user has sys_admin role
  const checkAdminAccess = async () => {
    const hasAccess = await hasRole('sys_admin');
    setCanEditLogo(hasAccess);
  };

  useEffect(() => {
    checkAdminAccess();
  }, []);

  return (
    <div className="relative w-[150px] h-[75px] bg-white p-1.5 rounded-lg flex items-center justify-center">
      {cachedLogoUrl ? (
        <div 
          className={`relative group ${canEditLogo ? 'cursor-pointer' : ''} w-full h-full flex items-center justify-center`}
          onClick={() => canEditLogo && setIsLogoDialogOpen(true)}
        >
          <div className="w-full h-full flex items-center justify-center">
            <Image 
              src={cachedLogoUrl} 
              alt={`${companyName} logo`}
              className="max-h-[65px] max-w-[140px]"
              objectFit="contain"
              enableOptimization={false}
              instant
              priority
            />
          </div>
          {canEditLogo && (
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
              <Camera className="h-5 w-5 text-white" />
            </div>
          )}
        </div>
      ) : (
        <div 
          className={`w-full h-full bg-muted rounded-lg flex items-center justify-center group relative ${
            canEditLogo ? 'cursor-pointer' : ''
          }`}
          onClick={() => canEditLogo && setIsLogoDialogOpen(true)}
        >
          <ImageIcon className="w-8 h-8 text-muted-foreground" />
          {canEditLogo && (
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
              <Camera className="h-5 w-5 text-white" />
            </div>
          )}
        </div>
      )}

      <LogoDialog 
        isOpen={isLogoDialogOpen}
        onClose={() => setIsLogoDialogOpen(false)}
        companyId={companyId}
        currentLogoUrl={logoUrl || ''}
        onLogoUpdate={onLogoUpdate || (() => {})}
      />
    </div>
  );
}
